import {
  AutoCompleteShipInfo,
  DetectedEvent,
  useAcknowledgeEvent,
  useGotoEventFetch,
} from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useAISMapContext } from '../Map/AISMapContext';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { useShapeFetch } from '@ais-ui/api';
import { transform } from 'ol/proj';
import React, { useCallback } from 'react';
import { cn } from '@ais-ui/utils';
import { PiDotsThreeVertical } from 'react-icons/pi';

export const EventDropDownMenu = ({
  event,
  hideSelectShip = false,
}: {
  event: DetectedEvent;
  hideSelectShip?: boolean;
}) => {
  const { messages } = useLocalization();
  const { mutate } = useAcknowledgeEvent();
  const { setZoomToShip } = useAISMapContext();

  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const { toast } = useToast();

  const { addLayer, doesLayerExist, zoomToLayer } = useLayers();

  const shapeFetch = useShapeFetch();
  const gotoEventFetch = useGotoEventFetch();

  const detectorShapeNameId = `Detector ${event.name}`;

  const onGotoEvent = async (eventId: string) => {
    const gotoEventData = await gotoEventFetch(eventId);
    const view = map.getView();

    const transformedCenter = transform(
      [gotoEventData.lon, gotoEventData.lat],
      dataProjection,
      displayProjection
    );

    view.setCenter(transformedCenter);
    view.setZoom(gotoEventData.zoomLevel);
  };

  const onShowShape = useCallback(
    async (shapeId: string) => {
      if (doesLayerExist(detectorShapeNameId)) {
        return;
      }

      const shape = await shapeFetch(shapeId);

      const scopedVectorLayer = shapeToVectorLayer(
        shape,
        displayProjection,
        dataProjection
      );

      addLayer(
        scopedVectorLayer,
        detectorShapeNameId,
        true,
        true,
        true,
        shape.properties,
        detectorShapeNameId
      );
      zoomToLayer(scopedVectorLayer);
    },
    [doesLayerExist, shapeFetch, addLayer, zoomToLayer, detectorShapeNameId]
  );

  const onSelectShip = async () => {
    const newSelectedShip: AutoCompleteShipInfo = {
      callSign: event.callSign ? event.callSign : null,
      imo: event.imo ? event.imo : null,
      mmsi: event.mmsi ? event.mmsi.toString() : null,
      name: event.shipName ? event.shipName : null,
      type: 'TT_SHIP',
    };
    setZoomToShip(newSelectedShip);
  };

  const onAcknowledge = async () => {
    mutate(event.id, {
      onSuccess: () => {
        toast({
          title: 'Success!',
          description: messages.eventsAcknowledgedEventSuccess,
        });
      },
      onError: () => {
        toast({
          title: 'Error!',
          description: messages.eventsAcknowledgedEventError,
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="size-7">
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!hideSelectShip && (
          <DropdownMenuItem onClick={onSelectShip}>
            {messages.eventsActionsSelectShip}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onShowShape(event.shapeUid)}>
          {messages.eventsActionsShowDetector}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onGotoEvent(event.id)}>
          {messages.eventsActionsShowEvent}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={event.ack_state === 2}
          onClick={onAcknowledge}
        >
          {messages.eventsActionsAcknowledge}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
